// Sidebar redesign — track visits to NEW-pill items so the pill auto-clears
// after the user has navigated to the destination ≥3 times. Persisted in
// localStorage; cheap; no backend.

import { useEffect, useState } from 'react';

export const SIDEBAR_VISITS_STORAGE_KEY = 'coldcaller:sidebar-visits';
export const NEW_PILL_HIDE_THRESHOLD = 3;

type VisitMap = Record<string, number>;

function readVisits(): VisitMap {
  if (typeof window === 'undefined') return {};
  try {
    const raw = window.localStorage.getItem(SIDEBAR_VISITS_STORAGE_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as VisitMap;
    }
    return {};
  } catch {
    return {};
  }
}

function writeVisits(map: VisitMap): void {
  if (typeof window === 'undefined') return;
  try {
    window.localStorage.setItem(SIDEBAR_VISITS_STORAGE_KEY, JSON.stringify(map));
  } catch {
    /* noop */
  }
}

/**
 * Increment the visit count for `path` exactly once per pathname change.
 * Called from the sidebar root with `useLocation().pathname`.
 */
export function recordSidebarVisit(path: string): void {
  const map = readVisits();
  map[path] = (map[path] ?? 0) + 1;
  writeVisits(map);
}

/**
 * Read-only — returns whether `path` has been visited ≥ NEW_PILL_HIDE_THRESHOLD
 * times. Re-evaluates on each render of consumers via the version state.
 */
export function useNewPillEligibility(): (path: string) => boolean {
  // version forces a re-read after writes within the same session.
  const [, setVersion] = useState(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onStorage = (e: StorageEvent) => {
      if (e.key === SIDEBAR_VISITS_STORAGE_KEY) {
        setVersion((v) => v + 1);
      }
    };
    window.addEventListener('storage', onStorage);
    return () => window.removeEventListener('storage', onStorage);
  }, []);

  return (path: string) => {
    const map = readVisits();
    return (map[path] ?? 0) < NEW_PILL_HIDE_THRESHOLD;
  };
}
